import { useCallback, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';

import { ILocation } from '@rbi-ctg/frontend';
import { IStore } from '@rbi-ctg/store';
import { RbiOrderStatus, useGetUniqueStoresQuery } from 'generated/rbi-graphql';
import useAuth from 'hooks/auth/use-auth';
import { useFavoriteStores } from 'hooks/favorite-stores';
import useDialogModal from 'hooks/use-dialog-modal';
import useErrorModal from 'hooks/use-error-modal';
import {
  StoreAction,
  searchFavRequested,
  searchFavsError,
  searchFavsSuccess,
  searchNearbyError,
  searchNearbyRequested,
  searchNearbySuccess,
  searchRecentError,
  searchRecentRequested,
  searchRecentSuccess
} from 'pages/store-locator/ducks/stores';
import {
  getAllRestaurantsByIds,
  getAllRestaurantsByIdsOrderedByLocation,
  getAllRestaurantsByStoreNumbers,
  getAllRestaurantsByStoreNumbersOrderedByLocation,
  getRestaurantsNearMe
} from 'remote/api/restaurants';
import { useNetworkContext } from 'state/network';
import { isValidPosition } from 'utils/geolocation';

interface IBounds {
  ne: ILocation;
  sw: ILocation;
}

interface ISearchStoresProps {
  neLat: number;
  neLng: number;
  swLat: number;
  swLng: number;
  center: ILocation;
  enableQueryRecentStores?: boolean;
  storeLocatorDispatch: StoreAction;
}

export default function useSearchStores({
  neLat,
  neLng,
  swLat,
  swLng,
  center,
  enableQueryRecentStores,
  storeLocatorDispatch
}: ISearchStoresProps) {
  const { connection, sanityEndpoints } = useNetworkContext();
  const [activeStoreId, setActiveStoreId] = useState('');
  const { favoriteStoreIds } = useFavoriteStores();
  const { isAuthenticated } = useAuth();
  const { data: uniqueStoreOrders, loading: uniqueStoreOrdersLoading } = useGetUniqueStoresQuery({
    skip: !enableQueryRecentStores || !isAuthenticated(),
    variables: {
      orderStatuses: [
        RbiOrderStatus.CATERING_SUCCESSFUL,
        RbiOrderStatus.CATERING_PLACED,
        RbiOrderStatus.INSERT_SUCCESSFUL,
        RbiOrderStatus.UPDATE_SUCCESSFUL
      ]
    }
  });

  const recentStoreIds = useMemo(() => {
    const uniqueStores = uniqueStoreOrders?.uniqueStores.orders?.map(order => order?.cart.storeId);

    return uniqueStores?.filter((id: string | undefined): id is string => !!id);
  }, [uniqueStoreOrders]);

  const [ErrorDialog, openErrorDialog] = useErrorModal({
    modalAppearanceEventMessage: 'Error: Fetching Restaurants Error'
  });
  const [Dialog, openDialog] = useDialogModal({
    modalAppearanceEventMessage: 'Notify user to enable location services'
  });
  const { formatMessage } = useIntl();
  const searchNearbyRestaurants = useCallback(
    (location: ILocation, bounds?: IBounds | null, deliveryOnly: boolean = false) => {
      storeLocatorDispatch(searchNearbyRequested());

      // location could be an empty object, so verify it has at least a lat
      // similar check happening in use-store-locator
      if (!location || (location && !location.lat)) {
        location = center;
      }

      const boundingBox = bounds
        ? {
            minLat: bounds.sw.lat,
            maxLat: bounds.ne.lat,
            minLng: bounds.sw.lng,
            maxLng: bounds.ne.lng
          }
        : { minLat: swLat, maxLat: neLat, minLng: swLng, maxLng: neLng };
      if (!isValidPosition(location)) {
        openDialog({
          message: formatMessage({ id: 'messageAllowLocation' }),
          title: formatMessage({ id: 'titleAllowLocation' })
        });
        return Promise.resolve();
      }

      const queryPromise: Promise<IStore[]> = getRestaurantsNearMe(sanityEndpoints.groq, {
        ...boundingBox,
        connection,
        userLat: location.lat,
        userLng: location.lng,
        deliveryOnly
      });

      return queryPromise
        .then((results: IStore[]) => {
          storeLocatorDispatch(searchNearbySuccess(results));
        })
        .catch((error: Error) => {
          storeLocatorDispatch(searchNearbyError(error));
          openErrorDialog({ message: 'Could not retrieve restaurants', error });
        });
    },
    // eslint-disable-next-line
    [
      storeLocatorDispatch,
      sanityEndpoints.groq,
      connection,
      openDialog,
      formatMessage,
      openErrorDialog
    ]
  );

  const searchFavRestaurants = useCallback(
    (location: ILocation | null) => {
      storeLocatorDispatch(searchFavRequested());
      // NOTE: location could be empty object, so just verify it has at least "lat"
      // similar check happening in use-store-locator
      const queryPromise: Promise<IStore[]> =
        location && location.lat
          ? getAllRestaurantsByIdsOrderedByLocation(sanityEndpoints.groq, {
              connection,
              storeIds: favoriteStoreIds,
              userLat: location.lat,
              userLng: location.lng
            })
          : getAllRestaurantsByIds(sanityEndpoints.groq, {
              connection,
              storeIds: favoriteStoreIds
            });
      return queryPromise
        .then((results: IStore[]) => {
          storeLocatorDispatch(searchFavsSuccess(results));
        })
        .catch((error: Error) => {
          storeLocatorDispatch(searchFavsError(error));
          openErrorDialog({ message: 'Could not retrieve favorite restaurants', error });
        });
    },
    [connection, favoriteStoreIds, openErrorDialog, sanityEndpoints.groq, storeLocatorDispatch]
  );

  const searchRecentRestaurants = useCallback(
    (location: ILocation | null) => {
      if (!isAuthenticated()) {
        storeLocatorDispatch(searchRecentSuccess([]));
        return;
      }
      storeLocatorDispatch(searchRecentRequested());
      // If the user has no recentStoreIds and the uniqueStoreOrders query is not loading
      // anymore, set the user recent list to an empty one
      if (!recentStoreIds) {
        if (!uniqueStoreOrdersLoading) {
          storeLocatorDispatch(searchRecentSuccess([]));
          return;
        }
        return;
      }
      const queryPromise: Promise<IStore[]> =
        location && location.lat
          ? getAllRestaurantsByStoreNumbersOrderedByLocation(sanityEndpoints.groq, {
              connection,
              storeNumbers: recentStoreIds,
              userLat: location.lat,
              userLng: location.lng
            })
          : getAllRestaurantsByStoreNumbers(sanityEndpoints.groq, {
              connection,
              storeNumbers: recentStoreIds
            });
      return queryPromise
        .then((results: IStore[]) => {
          storeLocatorDispatch(searchRecentSuccess(results));
        })
        .catch((error: Error) => {
          storeLocatorDispatch(searchRecentError(error));
          openErrorDialog({ message: 'Could not retrieve recent restaurants', error });
        });
    },
    [
      connection,
      isAuthenticated,
      openErrorDialog,
      recentStoreIds,
      sanityEndpoints.groq,
      storeLocatorDispatch,
      uniqueStoreOrdersLoading
    ]
  );

  return {
    activeStoreId,
    ErrorDialog,
    Dialog,
    searchNearbyRestaurants,
    setActiveStoreId,
    searchFavRestaurants,
    searchRecentRestaurants
  };
}
